import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Container,
    Typography,
    Paper,
    Grid,
    Autocomplete,
    TextField,
    Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Chip,
    InputAdornment
} from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';

import LoadingBox from '../../utils/LoadingBox';
import {
    getPartnerTransferRates,
    updatePartnerTransferRates,
    getTransferSeasons
} from './RatesSlice';
import { getModels } from '../fleet/FleetSlice';

export default function PartnerTransferRates() {
    const { isLoading, partnerTransferRates, transferSeasons } = useSelector((state) => state.rates);
    const { models } = useSelector((state) => state.fleet);
    const dispatch = useDispatch();

    const [filterModel, setFilterModel] = useState(null);
    const [filterSeason, setFilterSeason] = useState(null);
    const [openMarkupDialog, setOpenMarkupDialog] = useState(false);
    const [markupPercent, setMarkupPercent] = useState('10');

    // Local edited rows for inline spreadsheet editing
    const [gridRows, setGridRows] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        dispatch(getPartnerTransferRates());
        dispatch(getTransferSeasons());
        dispatch(getModels());
    }, [dispatch]);

    useEffect(() => {
        if (partnerTransferRates?.rows) {
            const seasons = partnerTransferRates.seasons || [];
            const mappedRows = partnerTransferRates.rows.map((r) => {
                const rowObj = {
                    id: r.id,
                    vehicle: `${r.vehicle_make} ${r.vehicle_model}`,
                    vehicle_make: r.vehicle_make,
                    vehicle_model: r.vehicle_model,
                };
                seasons.forEach((s) => {
                    const key = `season_${s.id}`;
                    const def = r.default_rates?.[key] ?? '0.00';
                    const part = r.partner_rates?.[key] ?? null;
                    const eff = r.effective_rates?.[key] ?? def;

                    rowObj[`${key}_default`] = def;
                    rowObj[`${key}_partner`] = part !== null && part !== undefined ? parseFloat(part).toFixed(2) : '';
                    rowObj[`${key}_effective`] = eff;
                });
                return rowObj;
            });
            setGridRows(mappedRows);
            setHasUnsavedChanges(false);
        }
    }, [partnerTransferRates]);

    const seasons = (partnerTransferRates?.seasons && partnerTransferRates.seasons.length > 0)
        ? partnerTransferRates.seasons
        : (transferSeasons || []);

    const visibleSeasons = filterSeason ? seasons.filter((s) => s.id === filterSeason.id) : seasons;

    // Filter rows
    const displayRows = gridRows.filter((row) => {
        if (filterModel && row.id !== filterModel.id) return false;
        return true;
    });

    // Build columns with clear Season headers on each column
    const columns = [
        {
            field: 'vehicle',
            headerName: 'Vehicle Model',
            width: 220,
            renderCell: (params) => <strong>{params.value}</strong>
        }
    ];

    visibleSeasons.forEach((s) => {
        const key = `season_${s.id}`;
        columns.push(
            {
                field: `${key}_default`,
                headerName: `${s.name} - Default`,
                width: 110,
                type: 'number',
                headerAlign: 'center',
                align: 'right',
                renderHeader: () => (
                    <Box sx={{ textAlign: 'center', width: '100%', py: 0.5, lineHeight: 1.2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569' }}>
                            Default
                        </Typography>
                    </Box>
                ),
                renderCell: (params) => (
                    <Typography variant="body2" color="text.secondary">
                        {parseFloat(params.value || 0).toFixed(2)}
                    </Typography>
                )
            },
            {
                field: `${key}_partner`,
                headerName: `${s.name} - My Rate`,
                width: 120,
                editable: true,
                type: 'number',
                headerAlign: 'center',
                align: 'right',
                renderHeader: () => (
                    <Box sx={{ textAlign: 'center', width: '100%', py: 0.5, lineHeight: 1.2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'primary.dark' }}>
                            My Rate
                        </Typography>
                    </Box>
                ),
                renderCell: (params) => {
                    const isCustom = params.value !== '' && params.value !== null && params.value !== undefined;
                    return (
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isCustom ? 700 : 400,
                                color: isCustom ? 'primary.main' : 'text.disabled',
                                fontStyle: isCustom ? 'normal' : 'italic'
                            }}
                        >
                            {isCustom ? parseFloat(params.value).toFixed(2) : `(Default)`}
                        </Typography>
                    );
                },
                valueSetter: (params) => {
                    const rawVal = params.value;
                    if (rawVal === '' || rawVal === null || rawVal === undefined) {
                        return {
                            ...params.row,
                            [`${key}_partner`]: '',
                            [`${key}_effective`]: params.row[`${key}_default`]
                        };
                    }
                    const parsed = parseFloat(rawVal);
                    if (!isNaN(parsed) && parsed >= 0) {
                        return {
                            ...params.row,
                            [`${key}_partner`]: parsed.toFixed(2),
                            [`${key}_effective`]: parsed.toFixed(2)
                        };
                    }
                    return params.row;
                }
            },
            {
                field: `${key}_effective`,
                headerName: `${s.name} - Effective`,
                width: 110,
                type: 'number',
                headerAlign: 'center',
                align: 'right',
                renderHeader: () => (
                    <Box sx={{ textAlign: 'center', width: '100%', py: 0.5, lineHeight: 1.2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1e293b' }}>
                            Effective
                        </Typography>
                    </Box>
                ),
                renderCell: (params) => (
                    <Chip
                        label={parseFloat(params.value || 0).toFixed(2)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                    />
                )
            }
        );
    });

    const processRowUpdate = (newRow) => {
        setGridRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? { ...row, ...newRow } : row))
        );
        setHasUnsavedChanges(true);
        return newRow;
    };

    const handleSaveMatrix = () => {
        const payloadRows = gridRows.map((r) => {
            const partnerRatesObj = {};
            seasons.forEach((s) => {
                const rawVal = r[`season_${s.id}_partner`];
                if (rawVal !== '' && rawVal !== null && rawVal !== undefined) {
                    partnerRatesObj[`season_${s.id}`] = rawVal;
                }
            });
            return {
                id: r.id,
                partner_rates: partnerRatesObj
            };
        });

        dispatch(updatePartnerTransferRates({ rows: payloadRows }))
            .unwrap()
            .then(() => {
                setHasUnsavedChanges(false);
            });
    };

    const handleApplyMarkup = (e) => {
        e.preventDefault();
        const parsedPct = parseFloat(markupPercent);
        if (isNaN(parsedPct)) {
            toast.error('Please enter a valid markup percentage');
            return;
        }

        const percentChange = (parsedPct / 100).toFixed(4);
        dispatch(updatePartnerTransferRates({ action: 'apply_markup', percent_change: percentChange }))
            .unwrap()
            .then(() => {
                setOpenMarkupDialog(false);
            });
    };

    return (
        <Container maxWidth="xl">
            <Typography variant="h3" align="center" gutterBottom>
                My transfer rates
            </Typography>

            {/* Filter and Action Bar */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        value={filterModel}
                        onChange={(e, val) => setFilterModel(val)}
                        options={models && Array.isArray(models) ? models : []}
                        getOptionLabel={(opt) => opt ? `${opt.vehicle_make} ${opt.vehicle_model}` : ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Filter Vehicle Model" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        value={filterSeason}
                        onChange={(e, val) => setFilterSeason(val)}
                        options={seasons}
                        getOptionLabel={(opt) => opt ? `${opt.name} (${opt.start_date})` : ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Filter Season" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => dispatch(getPartnerTransferRates())}
                        size="medium"
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<PercentIcon />}
                        onClick={() => setOpenMarkupDialog(true)}
                        size="medium"
                    >
                        Apply Markup %
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveMatrix}
                        disabled={!hasUnsavedChanges}
                        size="medium"
                    >
                        Save Matrix Changes
                    </Button>
                </Grid>
            </Grid>

            {hasUnsavedChanges && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip label="Unsaved changes in grid" color="warning" size="small" />
                </Box>
            )}

            <Paper sx={{ p: 2, width: '100%', overflowX: 'auto' }}>
                <LoadingBox isLoading={isLoading} elements={gridRows} noneText="No partner transfer vehicles or active seasons available" />
                <DataGrid
                    rows={displayRows}
                    columns={columns}
                    columnHeaderHeight={52}
                    rowHeight={44}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(error) => toast.error(error.message)}
                    autoHeight
                    disableRowSelectionOnClick
                    initialState={{
                        pagination: { paginationModel: { page: 0, pageSize: 25 } }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f1f5f9',
                            borderBottom: '2px solid #cbd5e1'
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#f1f5f9',
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            borderRight: '1px solid #e2e8f0'
                        },
                        '& .MuiDataGrid-cell--editable': {
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.06)'
                            }
                        }
                    }}
                />
            </Paper>

            {/* Apply Markup Dialog */}
            <Dialog open={openMarkupDialog} onClose={() => setOpenMarkupDialog(false)} maxWidth="xs" fullWidth>
                <form onSubmit={handleApplyMarkup}>
                    <DialogTitle>Apply Markup to All Transfer Rates</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Calculate your custom transfer rates automatically by applying a percentage markup to all platform default rates.
                        </DialogContentText>
                        <TextField
                            label="Markup Percentage"
                            type="number"
                            value={markupPercent}
                            onChange={(e) => setMarkupPercent(e.target.value)}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            helperText="e.g. 15 for +15% over default rates (or -5 for discount)"
                            fullWidth
                            autoFocus
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenMarkupDialog(false)} color="secondary">Cancel</Button>
                        <Button type="submit" variant="contained" color="primary">Apply Markup</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Container>
    );
}
